import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import MarketingNav from "@/components/MarketingNav";
import QuoteForm from "@/components/QuoteForm";
import QuoteCTA from "@/components/QuoteCTA";
import Seo from "@/components/Seo";
import { BIZ } from "@/data/site";
import { getService, services } from "@/data/services";
import { ArrowLeft, ArrowRight, Check, Sparkles, Calculator } from "lucide-react";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const service = getService(slug);

  if (!service) return <Navigate to="/" replace />;

  const Icon = service.icon;
  const others = services.filter((s) => s.slug !== service.slug).slice(0, 4);
  const canonicalPath = service.seoSlug ? `/${service.seoSlug}/` : `/services/${service.slug}`;
  const seoTitle = service.seoTitle || service.title;

  return (
    <div className="bg-paper min-h-screen" data-testid={`service-detail-${service.slug}`}>
      <Seo
        title={`${seoTitle} in ${BIZ.city}, ${BIZ.stateShort}`}
        description={`${service.shortDesc} Serving ${BIZ.serviceAreas.slice(0, 3).join(", ")} and surrounding ${BIZ.region}.`}
        path={canonicalPath}
      />
      <MarketingNav />

      {/* GET A QUOTE — Top of every page */}
      <QuoteCTA />

      {/* HERO */}
      <section className="relative pt-6 md:pt-10 pb-16 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-50 via-white to-transparent pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <Link
            to="/#services"
            data-testid="back-to-services"
            className="inline-flex items-center gap-2 text-sm font-semibold text-stone-600 hover:text-brand-700 mb-8 group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            All services
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-3 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-brand-100 flex items-center justify-center">
                  <Icon className="w-7 h-7 text-brand-700" />
                </div>
                <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700">
                  Wynridge · Service
                </div>
              </div>
              <h1
                className="text-5xl md:text-6xl font-extrabold tracking-tight text-stone-900 leading-[1.02]"
                style={{ fontFamily: "Outfit" }}
                data-testid="service-title"
              >
                {seoTitle} in {BIZ.city}, {BIZ.stateShort}
              </h1>
              <p className="mt-6 text-xl md:text-2xl text-stone-700 leading-relaxed max-w-2xl">
                {service.hero}
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/pressure-washing-estimate-calculator/"
                  data-testid="service-hero-calc-btn"
                  className="group inline-flex items-center gap-3 px-8 py-4 bg-brand-600 hover:bg-electric-500 text-white font-semibold rounded-full transition-all hover:-translate-y-0.5 shadow-lg hover:shadow-xl"
                >
                  <Calculator className="w-4 h-4" />
                  Get an instant estimate
                </Link>
                <a
                  href="#quote"
                  data-testid="service-hero-quote-btn"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-white text-stone-900 font-semibold rounded-full border border-stone-200 hover:border-stone-300 hover:bg-stone-50 transition-all hover:-translate-y-0.5"
                >
                  Request a written quote
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Price card */}
            <div className="lg:col-span-4">
              <div
                data-testid="service-price-card"
                className="bg-white rounded-3xl p-8 border border-stone-200 shadow-lg"
              >
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">
                  {service.priceLabel}
                </div>
                <div
                  className="text-5xl font-extrabold text-stone-900 mt-2"
                  style={{ fontFamily: "Outfit" }}
                >
                  ${service.priceFrom}
                </div>
                {service.priceDetail && (
                  <div className="text-sm font-semibold text-brand-700 mt-3 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    {service.priceDetail}
                  </div>
                )}
                <div className="mt-6 border-t border-stone-100 pt-6 space-y-3">
                  <PriceRow label="Free written quote" />
                  <PriceRow label="Fully insured" />
                  <PriceRow label="Same-week scheduling" />
                  <PriceRow label="Satisfaction walk-through" />
                </div>
                <a
                  href="#quote"
                  data-testid="service-price-cta"
                  className="mt-8 block text-center px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white font-bold rounded-full transition-all hover:-translate-y-0.5 shadow-md"
                >
                  Book this service
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="py-16 md:py-20 bg-white border-y border-stone-100">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-4">
            Overview
          </div>
          <h2
            className="text-3xl md:text-4xl font-extrabold tracking-tight text-stone-900 leading-tight"
            style={{ fontFamily: "Outfit" }}
          >
            What&apos;s actually happening on the truck.
          </h2>
          <p
            className="mt-6 text-lg text-stone-600 leading-relaxed"
            data-testid="service-long-desc"
          >
            {service.longDesc}
          </p>
        </div>
      </section>

      {/* WHAT'S INCLUDED + PROCESS */}
      <section className="py-16 md:py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-4">
              What&apos;s included
            </div>
            <h3
              className="text-2xl md:text-3xl font-extrabold text-stone-900 leading-tight mb-8"
              style={{ fontFamily: "Outfit" }}
            >
              Every visit, every time.
            </h3>
            <ul className="space-y-4" data-testid="service-includes">
              {service.includes.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-brand-700" strokeWidth={3} />
                  </div>
                  <span className="text-stone-700 leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-4">
              Our process
            </div>
            <h3
              className="text-2xl md:text-3xl font-extrabold text-stone-900 leading-tight mb-8"
              style={{ fontFamily: "Outfit" }}
            >
              Simple. Careful. Repeatable.
            </h3>
            <ol className="space-y-6" data-testid="service-process">
              {service.process.map((step, i) => (
                <li key={step.step} className="flex gap-4">
                  <div className="w-9 h-9 rounded-full bg-brand-600 text-white text-sm font-extrabold flex items-center justify-center flex-shrink-0">
                    {i + 1}
                  </div>
                  <div>
                    <div
                      className="font-bold text-stone-900"
                      style={{ fontFamily: "Outfit" }}
                    >
                      {step.step}
                    </div>
                    <div className="text-stone-600 leading-relaxed mt-1">
                      {step.detail}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* IDEAL FOR */}
      <section className="py-16 md:py-20 bg-white border-t border-stone-100">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-4">
            Ideal for
          </div>
          <h3
            className="text-2xl md:text-3xl font-extrabold text-stone-900 leading-tight mb-8"
            style={{ fontFamily: "Outfit" }}
          >
            You&apos;re a great fit for this service if&hellip;
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="service-ideal-for">
            {service.idealFor.map((item) => (
              <div
                key={item}
                className="p-5 rounded-2xl border border-stone-200 bg-stone-50 flex items-start gap-3"
              >
                <div className="w-2 h-2 rounded-full bg-brand-500 mt-2 flex-shrink-0" />
                <span className="text-stone-700 leading-relaxed">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE FORM */}
      <QuoteForm />

      {/* OTHER SERVICES */}
      <section className="py-16 md:py-24 bg-stone-50 border-t border-stone-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-3">
                Other services
              </div>
              <h3
                className="text-2xl md:text-3xl font-extrabold text-stone-900 leading-tight"
                style={{ fontFamily: "Outfit" }}
              >
                While we&apos;re on your property&hellip;
              </h3>
            </div>
            <Link
              to="/#services"
              className="text-sm font-bold text-brand-700 hover:text-brand-800 inline-flex items-center gap-2"
            >
              See all
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
            data-testid="related-services"
          >
            {others.map((o) => {
              const OI = o.icon;
              return (
                <Link
                  key={o.slug}
                  to={o.seoSlug ? `/${o.seoSlug}/` : `/services/${o.slug}`}
                  data-testid={`related-service-${o.slug}`}
                  className="p-6 rounded-2xl bg-white border border-stone-200 hover:border-brand-300 hover:shadow-lg transition-all group flex flex-col"
                >
                  <div className="w-11 h-11 rounded-2xl bg-brand-100 group-hover:bg-brand-600 flex items-center justify-center transition-colors">
                    <OI className="w-5 h-5 text-brand-700 group-hover:text-white transition-colors" />
                  </div>
                  <div
                    className="mt-4 font-bold text-stone-900"
                    style={{ fontFamily: "Outfit" }}
                  >
                    {o.title}
                  </div>
                  <div className="text-sm text-stone-500 mt-1">
                    from ${o.priceFrom}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-stone-900 text-stone-400 py-12 pb-28 md:pb-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col items-center md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="rounded-2xl overflow-hidden bg-white p-2 shadow-sm">
              <img
                src="/wynridge-logo.png?v=2"
                alt="Wynridge Exterior Cleaning"
                className="h-16 w-auto object-contain"
              />
            </div>
          </div>
          <div className="text-xs text-stone-500">
            © {new Date().getFullYear()} Wynridge Exterior Cleaning
          </div>
        </div>
      </footer>
    </div>
  );
}

function PriceRow({ label }) {
  return (
    <div className="flex items-center gap-2 text-sm text-stone-700">
      <Check className="w-4 h-4 text-brand-600" strokeWidth={3} />
      <span>{label}</span>
    </div>
  );
}
